<!-- 
Levy collection sandbox.
Because sandboxes cannot execute any workflow, and because the emphasis is on risk factors and other
setup, most of the functionality of a levy collection sandbox is displayed on this one screen.
-->
<template>
  <div>
    <our-heading 
      :breadcrumbs="getBreadcrumbs('')"
      :subtitle="statusDescription"
      title="Sandbox"
      subtitle-tooltip="Levy collection status"
      user-guide-topic="levy-collection-sandbox"
    >
      <template v-slot:navigator>
        <levy-collection-navigator :levy-collection-id="levyCollectionId" sandbox />
      </template>
    </our-heading>
    <v-container fluid grid-list-xl pa-0>
      <v-layout wrap>
        <v-flex xs12 md6 lg12 xl3>
          <v-card flat class="home">
            <our-widget-toolbar fixed title="Total levy" />
            <v-card-text class="pb-3" style="height: 302px">
              <p class="display-2 text-xs-center pt-5">{{ totalLevy | currency }}</p>
              <up-down 
                :original="original.levyCollection.totalLevy"
                :current="totalLevy"
                suffix="(compared to the original levy collection)"
                center
                large
              />
              <up-down 
                :original="previousTotalLevy"
                :current="totalLevy"
                suffix="(caused by your most recent edit)"
                center
                show-when-no-difference
              />
            </v-card-text>
          </v-card>
        </v-flex>
        <levy-components-widget
          :levy-collection-id="levyCollectionId" 
          :toggled="toggledLevyComponentsWidget"
          fixed
          @toggle="toggleLevyComponents()"
        />
        <provider-levy-amounts-widget
          :levy-collection-id="levyCollectionId" 
          :toggled="toggledProviderLevyAmountsWidget"
          fixed
          @toggle="toggleProviderLevyAmounts()"
        />
      </v-layout>
    </v-container>
    <v-card class="home mt-4">
      <v-layout row wrap>
        <v-flex xs12 md6 lg4>
          <h3 class="title mt-4 ml-4 pt-2">Fees & rates</h3>
          <fees-panel :levy-collection-id="levyCollectionId" for-sandbox />
        </v-flex>
        <v-flex xs12 md6 lg4>
          <h3 class="title mt-4 ml-4 pt-2">Timing</h3>
          <levy-calculation-timing-configuration-panel :levy-collection-id="levyCollectionId" for-sandbox />
        </v-flex>
        <div class="flex xs12 md6 lg4 pa-2">
          <risk-factor-table 
            :levy-collection-id="levyCollectionId"
            type="FinancialStrength" 
            title="Financial strength" 
            table-title="Financial strength score"
          />
        </div>
        <div class="flex xs12 md6 lg4 pa-2">
          <risk-factor-table 
            :levy-collection-id="levyCollectionId"
            type="NetProfitRatio" 
            title="Financial strength: Net profit ratio" 
            table-title="Net profit ratio"
            terminology="Score"
          />
        </div>
        <div class="flex xs12 md6 lg4 pa-2">
          <risk-factor-table 
            :levy-collection-id="levyCollectionId"
            type="ReturnOnAssets" 
            title="Financial strength: Return on assets" 
            table-title="Return on assets"
            terminology="Score"
          />
        </div>
        <div class="flex xs12 md6 lg4 pa-2">
          <risk-factor-table 
            :levy-collection-id="levyCollectionId"
            type="DebtToEquity" 
            title="Financial strength: Debt to equity" 
            table-title="Debt to equity"
            terminology="Score"
          />
        </div>
        <div class="flex xs12 md6 lg4 pa-2">
          <risk-factor-table 
            :levy-collection-id="levyCollectionId"
            is-percentage 
            type="CourseCompletion" 
            title="Completion rate" 
          />
        </div>
        <div class="flex xs12 md6 lg4 pa-2">
          <risk-factor-table 
            :levy-collection-id="levyCollectionId"
            type="NonComplianceHistory" 
            title="Non-compliance history: Late Payment measure" 
            table-title="Late payment measure"
          />
        </div>
        <div v-if="enableShortRegistrationPeriodRiskFactor" class="flex xs12 md6 lg4 pa-2">
          <risk-factor-table 
            :levy-collection-id="levyCollectionId"
            type="ShortRegistrationPeriod" 
            title="Non-compliance history: Registration period score" 
            table-title="Registration period score"
          />
        </div>
        <div class="flex xs12 md6 lg4 pa-2">
          <h3 class="subheading mb-0 ml-4 mt-4">Other non-compliance history settings</h3>
          <non-compliance-history-configuration-panel :levy-collection-id="levyCollectionId"/>
        </div>
      </v-layout>
    </v-card>
  </div>
</template>

<script>
import _ from "underscore";
import LevyCollectionNavigator from "@/components/levy-collections/LevyCollectionNavigator.vue";
import LevyComponentsWidget from "@/components/levy-collections/widgets/LevyComponentsWidget.vue";
import ProviderLevyAmountsWidget from "@/components/levy-collections/widgets/ProviderLevyAmountsWidget.vue";
import LevyCalculationTimingConfigurationPanel from "@/components/levy-collection-setup/LevyCalculationTimingConfigurationPanel.vue";
import FeesPanel from "@/components/levy-collection-setup/FeesPanel.vue";
import RiskFactorTable from "@/components/sandboxes/RiskFactorTable.vue";
import NonComplianceHistoryConfigurationPanel from "@/components/levy-collection-setup/NonComplianceHistoryConfigurationPanel.vue";
import UpDown from "@/components/sandboxes/UpDown.vue";
import levyCollectionMixins from "./mixins";
import filterMixins from "@/mixins/filter.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import * as mutation from "@/store/sandboxes/mutation-types";
import * as action from "@/store/sandboxes/action-types";

export default {
  components: {
    LevyCollectionNavigator,
    LevyComponentsWidget,
    ProviderLevyAmountsWidget,
    LevyCalculationTimingConfigurationPanel,
    FeesPanel,
    NonComplianceHistoryConfigurationPanel,
    RiskFactorTable,
    UpDown
  },
  mixins: [levyCollectionMixins, filterMixins, lookupMixins],
  data() {
    return {
      previousTotalLevy: null,
      mounting: true
    };
  },
  computed: {
    state() {
      return this.$store.state.sandboxes;
    },
    original() {
      return this.state.original;
    },
    busy() {
      return this.original.loading || this.loading;
    },
    totalLevy() {
      return this.$store.state.levyCollections.view.data.totalLevy;
    },
    toggledLevyComponentsWidget() {
      return this.state.dashboard.toggledLevyComponentsWidget;
    },
    toggledProviderLevyAmountsWidget() {
      return this.state.dashboard.toggledProviderLevyAmountsWidget;
    },
    enableShortRegistrationPeriodRiskFactor() {
      return this.levyCollection.enableShortRegistrationPeriodRiskFactor;
    },
    originalLoaded() {
      return this.original.loaded;
    },
    originalLevyCollectionId() {
      if (!this.lookupsLoaded) return null;
      const lc = _(this.lookups.allLevyCollections).find(c => c.sandboxId == this.levyCollectionId);
      return lc ? lc.id : null;
    },
    savingRiskFactors() {
      return this.$store.state.levyCollectionRiskFactors.edit.saving;
    },
    savingFees() {
      return this.$store.state.levyCollections.editFees.saving;
    }
  },
  watch: {
    originalLoaded() {
      this.reload();
    },
    originalLevyCollectionId() {
      this.reload();
    },
    totalLevy(newValue, oldValue) {
      if (!this.mounting) {
        this.previousTotalLevy = oldValue;
      }
    },
    savingRiskFactors(nowSaving) {
      if (nowSaving) {
        this.resetPreviousTotalLevy();
      }
    },
    savingFees(nowSaving) {
      if (nowSaving) {
        this.resetPreviousTotalLevy();
      }
    }
  },
  async mounted() {
    await this.reload();
    this.mounting = false;
  },
  methods: {
    async reload() {
      const needsReloaded =
        this.originalLevyCollectionId &&
        ((!this.loaded && !this.state.original.loading) ||
          this.state.original.levyCollectionId != this.originalLevyCollectionId);
      if (needsReloaded) {
        await this.$store.dispatch(action.LOAD_ORIGINAL_LEVY_COLLECTION, this.originalLevyCollectionId);
      }
    },
    toggleLevyComponents() {
      this.$store.commit(mutation.UPDATE_TOGGLES, {
        toggledLevyComponentsWidget: !this.toggledLevyComponentsWidget
      });
    },
    toggleProviderLevyAmounts() {
      this.$store.commit(mutation.UPDATE_TOGGLES, {
        toggledProviderLevyAmountsWidget: !this.toggledProviderLevyAmountsWidget
      });
    },
    resetPreviousTotalLevy() {
      this.previousTotalLevy = this.totalLevy;
    }
  }
};
</script>
